import type { InvoiceDeadline } from "../api/types";

const STATUS_CLASS: Record<InvoiceDeadline["status"], string> = {
  overdue: "bg-red-100 text-red-800",
  due_soon: "bg-amber-100 text-amber-800",
  on_time: "bg-green-100 text-green-800",
};

function duration(hours: number, daysOnly = false): string {
  const abs = Math.abs(hours);
  if (daysOnly) {
    // Compte Assurance : jours restants du traitement complet de la facture.
    const days = Math.max(1, Math.ceil(abs / 24));
    return `${days} jour${days > 1 ? "s" : ""}`;
  }
  return abs >= 48 ? `${Math.round(abs / 24)} j` : `${abs} h`;
}

/** Libellé court de l'échéance (exports, recherche). */
export function deadlineText(deadline: InvoiceDeadline | null | undefined): string {
  if (!deadline) return "Traitement terminé";
  const due = new Date(deadline.due_at.replace(" ", "T")).toLocaleDateString("fr-FR");
  const state =
    deadline.status === "overdue"
      ? `en retard de ${duration(deadline.remaining_hours, deadline.stage === "overall")}`
      : `reste ${duration(deadline.remaining_hours, deadline.stage === "overall")}`;
  return `${deadline.label} · échéance ${due} (${state})`;
}

/** Compte Assurance : « 7 jours restants » ou « En retard de 3 jours ». */
export function remainingDaysText(deadline: InvoiceDeadline): string {
  const days = duration(deadline.remaining_hours, true);
  if (deadline.status === "overdue") return `En retard de ${days}`;
  return `${days} restant${deadline.remaining_hours > 24 ? "s" : ""}`;
}

/** Statut d'une facture en cours pour le compte Assurance : uniquement les jours restants. */
export function RemainingDaysBadge({ deadline }: { deadline: InvoiceDeadline }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[deadline.status]}`}>
      {remainingDaysText(deadline)}
    </span>
  );
}

/**
 * Niveau de traitement en cours et son échéance : en retard (rouge), proche (orange), dans les temps (vert).
 */
export function DeadlineBadge({ deadline }: { deadline: InvoiceDeadline | null | undefined }) {
  if (!deadline) {
    return <span className="text-xs text-muted">Traitement terminé</span>;
  }
  const due = new Date(deadline.due_at.replace(" ", "T"));

  return (
    <div
      className="flex min-w-[9rem] flex-col gap-0.5"
      title={`${deadline.actor} · démarré le ${new Date(deadline.started_at.replace(" ", "T")).toLocaleString("fr-FR")}`}
    >
      <span className="text-xs font-medium text-foreground">{deadline.label}</span>
      <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLASS[deadline.status]}`}>
        {deadline.status === "overdue"
          ? `En retard de ${duration(deadline.remaining_hours, deadline.stage === "overall")}`
          : `Reste ${duration(deadline.remaining_hours, deadline.stage === "overall")}`}
      </span>
      <span className="text-[11px] text-muted">Échéance {due.toLocaleDateString("fr-FR")}</span>
    </div>
  );
}
